import { useContext, useState, useEffect, useMemo } from 'react';
import { toPng } from 'html-to-image';
import useDeviceDetect from '../helpers/screens';
import PeriodSelector from '../components/Analytics/PeriodSelector';
import StatsSwiper from '../components/Analytics/StatsSwiper';
import ChartSection from '../components/Analytics/ChartSection';
import { UserContext } from '../context/UserContext';
import TopSourcesSwiper from '../components/Analytics/TopSourcesSwiper';
import { TimeInterval } from '../types/types';
import { DownloadIcon } from '../svg';
import WorldMap from '../components/common/charts/WorldMap';
import RowChart from '../components/common/charts/RowChart';
import UpgradeBanner from '../components/common/UpgradeBackdrop/upgradeBanner';
import Tooltip from '../components/common/ToolTip';
import IconButton from '../components/common/IconButton';
import { useGeneralAnalyticsQuery } from '../hooks/queries/useAnalyticsQueries';
import useSubscription from '../hooks/useSubscription';
import { getCountryFlag } from '../utils/countryUtils';
import Loading from '../components/common/Loading';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import useI18n from '../hooks/useI18n';
import { useQueryClient } from '@tanstack/react-query';
import { useForceRefresh } from '../hooks/useForceRefresh';
import RefreshButton from '../components/common/RefreshButton';

const currentHost = window.location.origin;

const createLabel = (
  period: TimeInterval,
  t: (key: string) => string,
): string => {
  return t(
    `analytics.dates.activity${period.charAt(0).toUpperCase() + period.slice(1)}`,
  );
};

export interface AnalyticsData {
  bestPerformingPlatform: {
    platformName: string;
    changeInPercentage: number;
  };
  countryCounts: Array<{
    _id: string;
    count: number;
  }>;
  deviceTypeDistribution: {
    mobile: number;
    tablet: number;
    pc: number;
  };
  timeFrameView: Array<{
    _id: string;
    count: number;
  }>;
  topCity: string | null;
  topCountry: string | null;
  topSources: Array<{
    _id: string;
    count: number;
    percentage: number;
    previousPercentage: number;
  }>;
  totalClicked: {
    count: number;
    changeInPercentage: number;
  };
  bestTimeToEngage: string | null;
  totalClicks: number;
  uniqueClicks: number;
  conversionRate: number;
  clicksGrowth: number;
  bestPerformingDevice: string;
}

const MOCK_ANALYTICS_DATA: AnalyticsData = {
  bestPerformingPlatform: {
    platformName: 'Instagram',
    changeInPercentage: 10,
  },
  countryCounts: [
    { _id: 'US', count: 850 },
    { _id: 'FR', count: 520 },
    { _id: 'GB', count: 460 },
    { _id: 'DE', count: 380 },
    { _id: 'TN', count: 320 },
    { _id: 'ES', count: 280 },
    { _id: 'NL', count: 245 },
    { _id: 'IT', count: 220 },
  ],
  deviceTypeDistribution: {
    mobile: 68,
    tablet: 12,
    pc: 20,
  },
  timeFrameView: [
    { _id: 'Jan', count: 120 },
    { _id: 'Feb', count: 180 },
    { _id: 'Mar', count: 240 },
    { _id: 'Apr', count: 310 },
    { _id: 'May', count: 420 },
    { _id: 'Jun', count: 380 },
  ],
  topCity: 'New York',
  topCountry: 'US',
  topSources: [
    { _id: 'Instagram', count: 850, percentage: 38, previousPercentage: 35 },
    { _id: 'Facebook', count: 640, percentage: 28, previousPercentage: 30 },
    { _id: 'Twitter', count: 320, percentage: 14, previousPercentage: 12 },
    { _id: 'LinkedIn', count: 280, percentage: 12, previousPercentage: 10 },
    { _id: 'TikTok', count: 180, percentage: 8, previousPercentage: 6 },
  ],
  totalClicked: {
    count: 2270,
    changeInPercentage: 8.5,
  },
  bestTimeToEngage: '6-9 AM',
  totalClicks: 2547,
  uniqueClicks: 1823,
  conversionRate: 12.5,
  clicksGrowth: 8.7,
  bestPerformingDevice: 'Mobile',
};

const Analytics: React.FC = () => {
  const { t, currentLanguage } = useI18n();
  const userContext = useContext(UserContext);
  const { isMobile } = useDeviceDetect();
  const [selectedRange, setSelectedRange] = useState<TimeInterval>('week');
  const [pdfInProgress, setPdfInProgress] = useState(false);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;
  const { hasSubscription } = useSubscription();

  const isRealFetch = !!hasSubscription && !isDummyUser;
  const {
    data: rawData,
    isLoading,
    error,
  } = useGeneralAnalyticsQuery(selectedRange, isRealFetch);

  const queryClient = useQueryClient();
  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    () =>
      queryClient.refetchQueries({
        queryKey: ['analytics', 'general'],
        type: 'active',
      }),
    'analytics-page',
  );

  useEffect(() => {
    if (error) {
      console.error('Error when fetching analytics data', error);
      toast.error(String(t('errors.somethingWentWrong')));
    }
  }, [error]);

  const analyticsData: AnalyticsData = useMemo(() => {
    if (!isRealFetch || !rawData) return MOCK_ANALYTICS_DATA;
    const apiData = rawData as any;
    return {
      bestPerformingPlatform: apiData.bestPerformingPlatform,
      countryCounts: apiData.countryCounts || [],
      deviceTypeDistribution: apiData.deviceTypeDistribution || {
        mobile: 0,
        tablet: 0,
        pc: 0,
      },
      timeFrameView: apiData.timeFrameView || [],
      topCity: apiData.topCity || null,
      topCountry: apiData.topCountry || null,
      topSources: apiData.topSources || [],
      totalClicked: apiData.totalClicked || {
        count: 0,
        changeInPercentage: 0,
      },
      bestTimeToEngage: apiData.bestTimeToEngage || null,
      totalClicks: apiData.totalClicked?.count || 0,
      uniqueClicks: 0,
      conversionRate: 0,
      clicksGrowth: apiData.totalClicked?.changeInPercentage || 0,
      bestPerformingDevice: apiData.bestPerformingDevice || 'Unknown',
    };
  }, [isRealFetch, rawData]);

  const removeElementsByClass = (className: string) => {
    const elements = document.getElementsByClassName(className);
    while (elements.length > 0) {
      elements[0].remove();
    }
  };

  const print = async () => {
    const input = document.getElementById('printArea');
    if (!input) return;

    setPdfInProgress(true);

    try {
      removeElementsByClass('apexcharts-ycrosshairs');
      removeElementsByClass('apexcharts-xcrosshairs');

      setTimeout(async () => {
        try {
          const dataUrl = await toPng(input, {
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
          });

          const pdf = new jsPDF('p', 'mm', 'a4');

          pdf.addImage(
            `${currentHost}/assets/Logo.png`,
            'PNG',
            5,
            5,
            30,
            6,
            undefined,
            'NONE',
          );
          pdf.addImage(dataUrl, 'PNG', 5, 12, 200, 200); // Fit the image to the page width
          pdf.save('Analytics_Report.pdf');
        } catch (error) {
          toast.error(String(t('messages.error.pdfDownloadError')));
          console.error('Error generating PDF after delay:', error);
        } finally {
          setPdfInProgress(false);
        }
      }, 2000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfInProgress(false);
    }
  };

  const minClicks =
    analyticsData.countryCounts.length > 0
      ? Math.min(...analyticsData.countryCounts.map((c) => c.count))
      : 0;

  const maxClicks =
    analyticsData.countryCounts.length > 0
      ? Math.max(...analyticsData.countryCounts.map((c) => c.count))
      : 0;

  const handlePeriodChange = (newPeriod: TimeInterval) => {
    setSelectedRange(newPeriod);
  };

  return (
    <div className="min-h-full bg-gray-50 relative max-w-full overflow-x-hidden">
      <div className="flex flex-col p-[24px] max-w-full">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-header text-[#172B4D]">
            {String(t('analytics.title'))}
          </h1>
          <div className="flex items-center gap-3">
            {isRealFetch && (
              <RefreshButton
                onRefresh={refresh}
                isRefreshing={isRefreshing}
                secondsRemaining={secondsRemaining}
              />
            )}
            {!isMobile && hasSubscription && (
              <IconButton className="w-[38px] h-[38px]">
                {pdfInProgress && (
                  <div className=" col-span-full flex justify-center items-center">
                    <div className="w-6 h-6 border-4 border-[#20e086] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                {!pdfInProgress && (
                  <Tooltip
                    text={String(t('analytics.downloadReport'))}
                    position="left"
                    className="!mb-0 "
                  >
                    <DownloadIcon className="" onClick={print} />
                  </Tooltip>
                )}
              </IconButton>
            )}
          </div>
        </div>
        {!hasSubscription && (
          <UpgradeBanner
            containerProps={{ className: '!justify-normal' }}
            headingText={String(t('upgrade.title'))}
            headingProps={{ className: '!mt-[356px]' }}
          />
        )}
        <div
          id="printArea"
          className="print:flex-col bg-gray-50 max-w-full w-full overflow-hidden"
        >
          <div
            className={`flex items-center justify-between mb-[8px] ${pdfInProgress ? 'mt-2' : 'mt-4'}`}
          >
            <label className="font-content font-[500] text-[#121111]">
              {createLabel(selectedRange, t)}
            </label>
            <PeriodSelector
              limit={selectedRange}
              setLimit={handlePeriodChange}
              isMobile={isMobile}
            />
          </div>
          {isLoading ? (
            <Loading
              centeredToParent={true}
              style={{
                top: 'calc(50% - 25px)',
              }}
            />
          ) : (
            <div className="max-w-full">
              <StatsSwiper analyticsData={analyticsData as any} />

              <ChartSection
                analyticsData={analyticsData as any}
                view={
                  selectedRange as 'daily' | 'weekly' | 'monthly' | 'yearly'
                }
              />

              {analyticsData.topSources.length === 0 ? null : (
                <div className="mt-[16px] mb-6">
                  <h2 className="font-[500] text-[#121111] mb-2">
                    {String(t('analytics.topSources'))}
                  </h2>
                  <TopSourcesSwiper topSources={analyticsData.topSources} />
                </div>
              )}

              <div className="border mt-4 md:col-span-2 md:flex rounded-2xl p-6 bg-white lg:max-h-[550px] overflow-hidden">
                <div className="relative flex-1 max-w-full">
                  <span className="text-[#4C4C4C] font-content whitespace-nowrap">
                    {String(t('analytics.usersTrafficByRegion'))}
                  </span>
                  <div className="flex flex-col justify-around mt-[5px]">
                    <div
                      className={`overflow-hidden md:h-[500px] aspect-[1.4] md:aspect-auto max-w-full`}
                    >
                      <WorldMap
                        countries={analyticsData.countryCounts.map((c) => ({
                          country: c._id,
                          clicks: c.count,
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Top Countries */}
                <div className="max-h-[400px] md:max-h-[550px] flex flex-col mt-4 lg:mt-0 md:pl-5 w-full flex-1 md:border-l md:border-[#E5E9EB] md:ml-4">
                  <span className="text-[#4C4C4C] font-content whitespace-nowrap mb-4">
                    {String(t('analytics.topCountries'))}
                  </span>
                  {analyticsData.countryCounts.length > 0 ? (
                    <div
                      className={`flex flex-col space-y-4 overflow-y-auto overflow-x-hidden ${pdfInProgress ? '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]' : ''}`}
                    >
                      {analyticsData.countryCounts
                        .filter((c) => c._id)
                        .sort((a, b) => {
                          if (b.count !== a.count) return b.count - a.count;
                          return a._id.localeCompare(b._id);
                        })
                        .map((c) => {
                          const regionNames = new Intl.DisplayNames(
                            [currentLanguage],
                            {
                              type: 'region',
                            },
                          );

                          // Toujours obtenir le nom anglais pour la récupération du drapeau
                          const englishRegionNames = new Intl.DisplayNames(
                            ['en'],
                            {
                              type: 'region',
                            },
                          );

                          let name, englishName;
                          try {
                            // Vérifier si le code pays est valide (2 caractères) avant de l'utiliser
                            if (
                              c._id &&
                              typeof c._id === 'string' &&
                              c._id.length === 2
                            ) {
                              name = regionNames.of(c._id);
                              englishName = englishRegionNames.of(c._id);
                            } else {
                              // Si le code n'est pas valide, utiliser une valeur par défaut
                              console.warn(`Invalid country code: ${c._id}`);
                              name = 'Unknown';
                              englishName = 'Unknown';
                            }
                          } catch (error) {
                            console.error('Error getting region name', error);
                            name = c._id || 'Unknown';
                            englishName = c._id || 'Unknown';
                          }

                          // Utiliser le nom anglais pour le drapeau
                          const flag = getCountryFlag(englishName ?? '');
                          return {
                            clicks: c.count,
                            country: name,
                            flag,
                          };
                        })
                        .map((countryData) => (
                          <RowChart
                            key={countryData.country}
                            data={{
                              country: countryData.country ?? 'unknown',
                              clicks: countryData.clicks,
                            }}
                            flag={countryData.flag}
                            minClicks={minClicks}
                            maxClicks={maxClicks}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full w-full">
                      <span className="text-gray-500 text-lg font-semibold">
                        {String(t('common.noData'))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
